import { useEffect, useRef, useState } from 'react';

/**
 * Shared logic for premium horizontal-scroll carousels (Categories, Featured, etc.).
 *
 *   const { scrollerRef, canPrev, canNext, progress, scrollByDir } =
 *     useHorizontalScroller([events.length]);
 *
 * Handles two tricky problems:
 *   1. Async layout — when items load async (API + images), the scroller's
 *      scrollWidth keeps growing after mount. A simple useEffect-on-mount
 *      misses those reflows and leaves `canNext` stuck at false. We attach
 *      a ResizeObserver + image load listeners to re-measure.
 *
 *   2. RTL scroll quirks — Chrome reports a NEGATIVE scrollLeft when the
 *      container is RTL (0 at the visual right edge, -maxScroll at the
 *      visual left edge). Firefox uses 0..max like LTR. Safari uses
 *      maxScroll..0 (descending). We normalize all three to a 0..1 logical
 *      progress where 0 = "at the start (visually rightmost in RTL,
 *      leftmost in LTR)" and 1 = "at the end".
 *
 *      scrollByDir('right'/'left') means "scroll the visible content one
 *      page in the user's reading direction":
 *        - LTR: 'right' = forward (positive scrollBy)
 *        - RTL: 'right' = backward (positive scrollBy still — but the
 *          visual "next" arrow in RTL is the LEFT arrow). Callers should
 *          map their arrow click to "left"=back, "right"=forward
 *          regardless of language; the hook handles the sign.
 */

function isRtl(el) {
  return getComputedStyle(el).direction === 'rtl';
}

/** Logical scrollLeft: 0..max where 0 = start, max = end, in any browser/dir. */
function logicalScrollLeft(el) {
  const max = el.scrollWidth - el.clientWidth;
  if (!isRtl(el)) return el.scrollLeft;
  // Detect RTL scrollLeft mode
  const raw = el.scrollLeft;
  if (raw <= 0) {
    // Chrome/Edge: 0 at start, -max at end
    return -raw;
  }
  // Safari (or Firefox in some configurations): max at start, 0 at end
  // We can't fully distinguish here without sniffing; rely on observed values.
  return max - raw;
}

export default function useHorizontalScroller(deps = []) {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [progress, setProgress] = useState(0);

  const update = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const logical = logicalScrollLeft(el);
    setCanPrev(logical > 8);
    setCanNext(logical < max - 8);
    setProgress(max > 0 ? Math.min(1, Math.max(0, logical / max)) : 0);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    update();

    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
      Array.from(el.children).forEach((child) => ro.observe(child));
    }

    const imgs = el.querySelectorAll('img');
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener('load', update, { once: true });
    });

    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  /**
   * dir = 'forward' | 'back' (logical) OR legacy 'left' | 'right'.
   * Callers pass logical direction; we map to the correct sign for the
   * current `direction` of the scroller.
   *
   * For backwards compat with existing callers using 'left'/'right':
   *   - In LTR: 'left' = back, 'right' = forward (same as today)
   *   - In RTL: 'left' = forward (visual left arrow points to next content),
   *             'right' = back (visual right arrow scrolls back to start)
   */
  const scrollByDir = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.max(el.clientWidth * 0.85, 320);

    let logical; // 'forward' or 'back'
    if (dir === 'forward' || dir === 'back') {
      logical = dir;
    } else if (isRtl(el)) {
      logical = dir === 'left' ? 'forward' : 'back';
    } else {
      logical = dir === 'right' ? 'forward' : 'back';
    }

    // Map logical direction to a CSS scrollBy delta.
    // Chrome RTL uses negative scrollLeft to advance forward;
    // Firefox/Safari use positive. Detect by reading current scrollLeft sign.
    let delta = logical === 'forward' ? amount : -amount;
    if (isRtl(el)) {
      const raw = el.scrollLeft;
      // If at start in Chrome RTL, raw is 0 and forward = negative.
      // If at start in Firefox RTL, raw is 0 and forward = positive (same as LTR).
      // Heuristic: try negative first if at 0, else use sign of existing scroll.
      // Robust approach: do BOTH (the browser will clamp the wrong one to 0).
      const before = el.scrollLeft;
      el.scrollBy({ left: delta, behavior: 'smooth' });
      // Schedule a check; if nothing moved, try the opposite sign.
      setTimeout(() => {
        if (el.scrollLeft === before) {
          el.scrollBy({ left: -delta, behavior: 'smooth' });
        }
      }, 80);
      return;
    }

    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return { scrollerRef, canPrev, canNext, progress, scrollByDir, recomputeScroll: update };
}
